import React, { useState, useCallback, useEffect, useMemo } from 'react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';
import { RefreshCw, Download, PackageX, Tag, Truck, Package } from 'lucide-react';
import { ReportNotes } from '@/pages/reports/_Components/InfoTip';

type Reason = 'label_not_collected' | 'shipped_no_tracking' | 'not_packed';

interface SummaryRow { reason: Reason; label: string; orders: number; value: number; prepaid: number; }
interface OrderRow {
  order_name: string;
  order_date: string | null;
  days_waiting: number | null;
  customer_name: string | null;
  phone: string | null;
  total_price: number;
  payment: 'COD' | 'Prepaid';
  fulfillment_status: string;
  courier_status: string;
  courier_status_at: string | null;
  awb: string | null;
  reason: Reason;
  reason_label: string;
}
interface NotDispatchedReport {
  summary: SummaryRow[];
  total_orders: number;
  total_value: number;
  orders: OrderRow[];
}

const fmt = (n: number) => (n ?? 0).toLocaleString('en-IN');
const REASON_ICON: Record<Reason, React.ReactNode> = {
  label_not_collected: <Tag className="h-5 w-5" />,
  shipped_no_tracking: <Truck className="h-5 w-5" />,
  not_packed: <Package className="h-5 w-5" />,
};
const REASON_HINT: Record<Reason, string> = {
  label_not_collected: 'Courier label printed, parcel never scanned by the courier',
  shipped_no_tracking: 'Fulfilled in Shopify, but Clickpost has never sent an update',
  not_packed: 'Not fulfilled in Shopify and no courier label',
};

/**
 * Orders placed 2-30 days ago that still have not been collected by a courier.
 * For the warehouse / ops team -- this is dispatch work, not a calling list.
 */
const NotDispatchedReport: React.FC = () => {
  const [report, setReport] = useState<NotDispatchedReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState<Reason | 'all'>('all');
  const [payment, setPayment] = useState<'all' | 'COD' | 'Prepaid'>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.req({ url: '/reports/not-dispatched', method: api.Method.POST, data: {} }) as unknown as NotDispatchedReport;
      setReport(res);
    } catch {
      toastMessage('Failed to load the Not Dispatched report', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = useMemo(() => (report?.orders || []).filter(o =>
    (reason === 'all' || o.reason === reason) && (payment === 'all' || o.payment === payment)
  ), [report, reason, payment]);

  const downloadCsv = () => {
    const head = ['Order', 'Order Date', 'Days Waiting', 'Customer', 'Phone', 'Value', 'Payment',
                  'Reason', 'Shopify Fulfillment', 'Courier Status', 'Courier Status At', 'AWB'];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map(o => [
      o.order_name, o.order_date?.slice(0, 10), o.days_waiting, o.customer_name, o.phone, o.total_price,
      o.payment, o.reason_label, o.fulfillment_status, o.courier_status, o.courier_status_at?.slice(0, 16), o.awb,
    ].map(esc).join(','));
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `not_dispatched_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <PackageX className="h-6 w-6 text-brandColor" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Not Dispatched</h1>
            <p className="text-xs text-gray-500">Orders placed 2–30 days ago that no courier has collected yet</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={payment} onChange={e => setPayment(e.target.value as typeof payment)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 dark:bg-gray-800 dark:border-gray-600">
            <option value="all">COD + Prepaid</option>
            <option value="COD">COD only</option>
            <option value="Prepaid">Prepaid only</option>
          </select>
          <button onClick={fetchData} disabled={loading}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-gray-600">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /><span>Refresh</span>
          </button>
          <button onClick={downloadCsv} disabled={!rows.length}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-brandColor text-white rounded-lg disabled:opacity-50">
            <Download className="h-4 w-4" /><span>CSV</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <button onClick={() => setReason('all')}
                className={`text-left bg-white dark:bg-gray-800 rounded-xl border p-4 ${reason === 'all' ? 'border-brandColor ring-1 ring-brandColor' : 'border-gray-200 dark:border-gray-700'}`}>
          <p className="text-xs text-gray-500">All stuck orders</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{fmt(report?.total_orders || 0)}</p>
          <p className="text-xs text-gray-400">₹{fmt(report?.total_value || 0)}</p>
        </button>
        {(report?.summary || []).map(s => (
          <button key={s.reason} onClick={() => setReason(s.reason)} title={REASON_HINT[s.reason]}
                  className={`text-left bg-white dark:bg-gray-800 rounded-xl border p-4 flex gap-3 ${reason === s.reason ? 'border-brandColor ring-1 ring-brandColor' : 'border-gray-200 dark:border-gray-700'}`}>
            <div className="p-2 rounded-lg bg-brandColor/10 text-brandColor h-fit">{REASON_ICON[s.reason]}</div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{fmt(s.orders)}</p>
              <p className="text-xs text-gray-400">₹{fmt(s.value)} · {fmt(s.prepaid)} prepaid</p>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 uppercase">
            <tr>
              {['Order', 'Ordered', 'Waiting', 'Customer', 'Phone', 'Value', 'Payment', 'Reason', 'Courier Status', 'AWB'].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.slice(0, 500).map(o => (
              <tr key={o.order_name} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{o.order_name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{o.order_date?.slice(0, 10)}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${(o.days_waiting ?? 0) >= 7 ? 'text-red-600 font-semibold' : ''}`}>{o.days_waiting}d</td>
                <td className="px-3 py-2 whitespace-nowrap">{o.customer_name || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{o.phone || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">₹{fmt(Math.round(o.total_price))}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded text-xs ${o.payment === 'Prepaid' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{o.payment}</span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{o.reason_label}</td>
                <td className="px-3 py-2 whitespace-nowrap">{o.courier_status}</td>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{o.awb || '—'}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">{loading ? 'Loading…' : 'Nothing stuck'}</td></tr>
            )}
          </tbody>
        </table>
        {rows.length > 500 && (
          <p className="px-3 py-2 text-xs text-gray-500">Showing the oldest 500 of {fmt(rows.length)} — download the CSV for all.</p>
        )}
      </div>

      <ReportNotes items={[
        { term: 'Why these are stuck', def: 'Couriers normally collect an order within ~31 hours of it being placed, so anything here has waited too long.' },
        { term: 'Label made, not collected', def: 'The parcel has a courier label (AWB) but was never scanned by the courier — check with the warehouse and the courier.' },
        { term: 'Marked shipped, no tracking', def: 'Fulfilled in Shopify but Clickpost has sent nothing. The nightly Clickpost check refreshes these first, so what remains is genuinely unknown.' },
        { term: 'Not packed', def: 'Not fulfilled in Shopify and no courier label.' },
        { term: 'Prepaid', def: 'Most urgent: the customer has already paid.' },
      ]} />
    </div>
  );
};

export default NotDispatchedReport;
